import axios, { AxiosError } from 'axios';

import {
  REDUX_CLIENTCODE,
  REDUX_SERVICE_ENDPOINTS,
  REDUX_SESSIONKEY,
} from 'constants/persistence';
import { getServiceEndpoints } from 'services/ErplyAPI/getServiceEndpoints';
import { proxy } from 'services/shared';
import { ObfuscatedLocalStorage } from 'utils/ObfuscatedLocalStorage';

import {
  SessionKeyInfoResponse,
  RequestProps,
  isSuccessfulResponse,
  SuccessResponse,
} from './types';

function getLocalstorage<T>(
  key: string,
  storage: typeof localStorage | typeof ObfuscatedLocalStorage,
  defaultValue: T,
) {
  try {
    return JSON.parse(storage.getItem(key)!);
  } catch (e) {
    return defaultValue;
  }
}

const getUrl = async (): Promise<string> => {
  const lsUrl = getLocalstorage(REDUX_SERVICE_ENDPOINTS, localStorage, {})
    ?.auth;
  if (lsUrl?.url) return lsUrl.url;
  const [payload] = await getServiceEndpoints();
  return payload?.['auth']?.url;
};

const doRequest = async <D, R>({
  endpoint,
  method,
  data,
  params,
  withCredentials,
}: RequestProps<D>): Promise<R> => {
  const clientCode = getLocalstorage(
    REDUX_CLIENTCODE,
    ObfuscatedLocalStorage,
    '',
  );
  const sessionKey = getLocalstorage(
    REDUX_SESSIONKEY,
    ObfuscatedLocalStorage,
    '',
  );

  const url = await getUrl();
  return axios({
    url: `${proxy}${url}${endpoint}`,
    method,
    data,
    params,
    headers: { clientCode, sessionKey },
    withCredentials,
  }).then(res => res.data);
};

export const getSessionKeyInfo = async () => {
  return doRequest<never, SessionKeyInfoResponse>({
    method: 'GET',
    endpoint: 'v1/session',
    withCredentials: true,
  }).then(res => {
    if (isSuccessfulResponse(res)) {
      return res.data;
    }
    throw new Error(res.status.errorDescription);
  });
};
function errorFromResponse(err: AxiosError) {
  throw new Error(err.response?.data?.status?.errorDescription ?? err.message, {
    cause: err,
  });
}

export function initAdminSession() {
  return doRequest<
    never,
    SuccessResponse<{
      channel: 'PASSWORD' | 'EMAIL' | 'GOOGLE_APP' | 'MS_APP';
      token: string;
    }>
  >({
    method: 'GET',
    endpoint: 'v1/session/admin',
  }).then(res => res.data, errorFromResponse);
}

export function confirmAdminSession(data: { key: string; token: string }) {
  return doRequest<{ key: string; token: string }, null>({
    method: 'POST',
    endpoint: 'v1/session/admin',
    data,
  }).catch(errorFromResponse);
}

export function deleteAdminSession() {
  return doRequest<never, SuccessResponse<string>>({
    method: 'DELETE',
    endpoint: 'v1/session/admin',
  }).catch(errorFromResponse);
}

/**
 * Sets a HttpOnly cookie
 */
export async function createSession(credentials: {
  password: string;
  username: string;
}) {
  return doRequest<
    {
      password: string;
      username: string;
      sessionLength: number;
    },
    any
  >({
    method: 'POST',
    endpoint: 'v1/session',
    withCredentials: true,
    data: { ...credentials, sessionLength: 86400 },
  }).then(res => {
    if (isSuccessfulResponse(res)) {
      return res.data;
    }
    throw new Error(res.status.errorDescription);
  });
}

/**
 * Sets a HttpOnly cookie
 */
export async function switchSession(credentials: { userPin: 'string' }) {
  return doRequest<
    {
      userPin: 'string';
      sessionLength: number;
    },
    any
  >({
    method: 'POST',
    endpoint: 'v1/switch-session',
    withCredentials: true,
    data: { ...credentials, sessionLength: 86400 },
  }).then(res => {
    if (isSuccessfulResponse(res)) {
      return res.data;
    }
    throw new Error(res.status.errorDescription);
  });
}

/**
 * Deletes a HttpOnly cookie
 */
export async function deleteSession() {
  return doRequest<never, string>({
    method: 'DELETE',
    endpoint: 'v1/session',
    withCredentials: true,
  }).catch(errorFromResponse);
}
